import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Code2, Download, LogIn, Moon, Palette, Store } from "lucide-react";
import { toast } from "sonner";
import { useEffect } from "react";
import { create } from "zustand";
import { PLUGIN_CATALOG, type PluginId } from "@ide/shared";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Tip,
} from "@/components/ui/overlay";
import { del, errorMessage, get, put } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { syncInstalledEngineThemes } from "@/plugins/extensions/data";
import { usePluginAction, usePlugins } from "@/plugins/use-plugins";
import { vscodeKeys } from "@/plugins/vscode-imported";
import { navigateTo } from "./bridge";
import { hasFeature } from "./edition";

/** Temas do catálogo (um ligado por vez; nenhum = o escuro padrão). */
export const THEME_IDS = PLUGIN_CATALOG.filter((p) => p.id.startsWith("kompil.theme-")).map(
  (p) => p.id as PluginId,
);

/** Menu Configurações › Temas abre este seletor (em vez de levar para a loja). */
export const useThemePicker = create<{ open: boolean; setOpen(open: boolean): void }>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

interface ImportedListing {
  id: string;
  themes: Array<{ label: string; dark: boolean; preview: string[] }>;
  activeTheme: string | null;
}

/** Temas trazidos do VS Code/Cursor (um ativo por vez; vence os do Kompil enquanto ativo). */
function useImportedThemes(enabled: boolean) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: vscodeKeys.list,
    queryFn: () => get<{ extensions: ImportedListing[] }>("/me/vscode-extensions"),
    enabled,
    staleTime: 60_000,
  });
  const themes = (data?.extensions ?? []).flatMap((e) =>
    e.themes.map((t) => ({ ...t, extensionId: e.id, active: e.activeTheme === t.label })),
  );
  const set = useMutation({
    mutationFn: (v: { extensionId: string; label: string } | null) =>
      v ? put("/me/vscode-extensions/theme", v) : del("/me/vscode-extensions/theme"),
    // vscodeKeys.list é prefixo de vscodeKeys.content: o editor e a interface trocam juntos.
    onSettled: () => void qc.invalidateQueries({ queryKey: vscodeKeys.list }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  return { themes, set };
}

/**
 * Troca rápida de tema, como o Ctrl+K Ctrl+T do VS Code: os temas instalados com um clique, e os
 * outros do catálogo para instalar e usar na hora. Fica na barra de título.
 */
export function ThemePicker({ className, hiddenTrigger }: { className?: string; hiddenTrigger?: boolean }) {
  const open = useThemePicker((s) => s.open);
  const setOpen = useThemePicker((s) => s.setOpen);
  const { data: me } = useMe();
  const guest = !me || me.user.isGuest;
  const { data: plugins } = usePlugins(hasFeature("plugins") && !guest);
  const action = usePluginAction();
  const imported = useImportedThemes(hasFeature("plugins") && !guest);
  // Temas de extensões já instaladas no motor (antes desta versão ou por fora) entram na lista.
  useEffect(() => {
    if (open && !guest && hasFeature("plugins")) void syncInstalledEngineThemes().catch(() => {});
  }, [open, guest]);
  if (!hasFeature("plugins")) return null;
  const themes = (plugins ?? []).filter((p) => THEME_IDS.includes(p.id as PluginId));
  const installed = themes.filter((p) => p.installed);
  const others = themes.filter((p) => !p.installed);
  const importedActive = imported.themes.find((t) => t.active) ?? null;
  // O importado do VS Code, quando ativo, é o que está na tela.
  const current = importedActive ? null : (installed.find((p) => p.enabled) ?? null);
  const currentName = importedActive?.label ?? current?.name ?? null;
  const useDefault = () => {
    if (importedActive) imported.set.mutate(null);
    for (const t of installed.filter((p) => p.enabled))
      action.mutate({ id: t.id as PluginId, action: "disable" });
  };
  const pick = (id: string, isInstalled: boolean) =>
    action.mutate({ id: id as PluginId, action: isInstalled ? "enable" : "install" });
  const item = "h-auto py-1.5";
  return (
    <Menu open={open} onOpenChange={setOpen}>
      {hiddenTrigger ? (
        // Sem botão próprio: o menu abre ancorado no Layout da barra de título.
        <MenuTrigger asChild>
          <span aria-hidden className="pointer-events-none absolute right-0 top-0 h-8 w-8" tabIndex={-1} />
        </MenuTrigger>
      ) : (
        <Tip label={`Tema${currentName ? `: ${currentName}` : ": escuro (padrão)"}`}>
          <MenuTrigger asChild>
            <button
              type="button"
              aria-label="Trocar o tema"
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-md text-fg-muted outline-none transition-colors hover:bg-bg-muted hover:text-fg focus-visible:ring-2 focus-visible:ring-ring/60 data-[state=open]:bg-bg-emphasis data-[state=open]:text-fg",
                className,
              )}
            >
              <Palette className="h-[16px] w-[16px]" strokeWidth={1.6} />
            </button>
          </MenuTrigger>
        </Tip>
      )}
      <MenuContent align="end" className="max-h-[70vh] w-72 overflow-y-auto">
        <MenuLabel>Tema</MenuLabel>
        {guest ? (
          <MenuItem
            icon={<LogIn className="h-3.5 w-3.5" />}
            onSelect={() =>
              window.dispatchEvent(
                new CustomEvent("kompil:account-required", { detail: "Entre para trocar o tema da IDE." }),
              )
            }
          >
            Entrar para trocar o tema
          </MenuItem>
        ) : (
          <>
            <MenuItem className={item} icon={<Moon className="h-3.5 w-3.5" />} onSelect={useDefault}>
              <span className="flex flex-1 items-center justify-between gap-2">
                Escuro (padrão)
                <Check className={cn("h-3.5 w-3.5", currentName ? "opacity-0" : "opacity-100")} />
              </span>
            </MenuItem>
            {installed.map((t) => (
              <MenuItem
                key={t.id}
                className={item}
                icon={<Palette className="h-3.5 w-3.5" />}
                onSelect={() => pick(t.id, true)}
              >
                <span className="flex flex-1 items-center justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block truncate">{t.name.replace(/^Tema /, "")}</span>
                    <span className="block truncate text-[11px] text-fg-subtle">{t.tagline}</span>
                  </span>
                  <Check
                    className={cn(
                      "h-3.5 w-3.5 shrink-0",
                      t.enabled && !importedActive ? "opacity-100" : "opacity-0",
                    )}
                  />
                </span>
              </MenuItem>
            ))}
            {imported.themes.length ? (
              <>
                <MenuSeparator />
                <MenuLabel>Do VS Code</MenuLabel>
                {imported.themes.map((t) => (
                  <MenuItem
                    key={`${t.extensionId}:${t.label}`}
                    className={item}
                    icon={<Code2 className="h-3.5 w-3.5" />}
                    onSelect={() => imported.set.mutate({ extensionId: t.extensionId, label: t.label })}
                  >
                    <span className="flex flex-1 items-center justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block truncate">{t.label}</span>
                        <span className="block truncate text-[11px] text-fg-subtle">
                          {t.dark ? "Escuro" : "Claro"} · importado
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-0.5">
                        {t.preview.slice(0, 3).map((c) => (
                          <span key={c} className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />
                        ))}
                        <Check className={cn("ml-1 h-3.5 w-3.5", t.active ? "opacity-100" : "opacity-0")} />
                      </span>
                    </span>
                  </MenuItem>
                ))}
              </>
            ) : null}
            {others.length ? (
              <>
                <MenuSeparator />
                <MenuLabel>Instalar e usar</MenuLabel>
                {others.map((t) => (
                  <MenuItem
                    key={t.id}
                    className={item}
                    icon={<Download className="h-3.5 w-3.5" />}
                    onSelect={() => pick(t.id, false)}
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{t.name.replace(/^Tema /, "")}</span>
                      <span className="block truncate text-[11px] text-fg-subtle">{t.tagline}</span>
                    </span>
                  </MenuItem>
                ))}
              </>
            ) : null}
          </>
        )}
        <MenuSeparator />
        <MenuItem
          icon={<Store className="h-3.5 w-3.5" />}
          onSelect={() => navigateTo("/plugins?category=aparencia")}
        >
          Ver temas na loja (com prévia)
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
